import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import axios from 'axios';
import toast from 'react-hot-toast';
import { 
  Plus, 
  Eye, 
  Edit, 
  Trash2, 
  QrCode,
  Calendar,
  MapPin,
  Users,
  Power
} from 'lucide-react';
import { format } from 'date-fns';

interface Event {
  _id: string;
  name: string;
  description: string;
  coupleNames: {
    bride: string;
    groom: string;
  };
  eventDate: string;
  venue: {
    name: string;
    address: string;
    city: string;
  };
  isActive: boolean;
  siteUrl: string;
  stats: {
    totalPhotos: number;
    totalUsers: number;
  };
  createdAt: string;
}

const Events: React.FC = () => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const queryClient = useQueryClient();

  const { data: events, isLoading } = useQuery<Event[]>(
    'events',
    () => axios.get('/api/events').then(res => res.data.data)
  );

  const toggleActiveMutation = useMutation(
    ({ eventId, isActive }: { eventId: string; isActive: boolean }) =>
      axios.put(`/api/events/${eventId}`, { isActive }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('events');
        toast.success('סטטוס האירוע עודכן בהצלחה');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'שגיאה בעדכון סטטוס האירוע');
      },
    }
  );

  const deleteEventMutation = useMutation(
    (eventId: string) => axios.delete(`/api/events/${eventId}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('events');
        toast.success('אירוע נמחק בהצלחה');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'שגיאה במחיקת האירוע');
      },
    }
  );

  const handleDeleteEvent = (event: Event) => {
    if (window.confirm(`האם אתה בטוח שברצונך למחוק את האירוע "${event.name}"?`)) {
      deleteEventMutation.mutate(event._id);
    }
  };

  const EventCard = ({ event }: { event: Event }) => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="text-lg font-semibold text-gray-900">{event.name}</h3>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              event.isActive 
                ? 'bg-green-100 text-green-800' 
                : 'bg-gray-100 text-gray-800'
            }`}>
              {event.isActive ? 'פעיל' : 'לא פעיל'}
            </span>
          </div>
          
          <div className="space-y-2 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              <span>{event.coupleNames.bride} & {event.coupleNames.groom}</span>
            </div>
            
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              <span>{format(new Date(event.eventDate), 'dd/MM/yyyy')}</span>
            </div>
            
            {event.venue?.name && (
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                <span>{event.venue.name}</span>
              </div>
            )}
          </div>

          <div className="mt-4 flex items-center gap-4 text-sm text-gray-500">
            <span>{event.stats?.totalPhotos || 0} תמונות</span>
            <span>{event.stats?.totalUsers || 0} משתמשים</span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Link
            to={`/events/${event._id}`}
            className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
            title="צפה באירוע"
          >
            <Eye className="h-4 w-4" />
          </Link>
          
          <Link
            to={`/events/${event._id}/texts`}
            className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-md transition-colors"
            title="ערוך טקסטים"
          >
            <Edit className="h-4 w-4" />
          </Link>
          
          <button
            onClick={() => setSelectedEvent(event)}
            className="p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-md transition-colors"
            title="QR Code"
          >
            <QrCode className="h-4 w-4" />
          </button>
          
          <button
            onClick={() => toggleActiveMutation.mutate({ eventId: event._id, isActive: !event.isActive })}
            disabled={toggleActiveMutation.isLoading}
            className={`p-2 rounded-md transition-colors ${
              event.isActive
                ? 'text-green-600 hover:text-orange-600 hover:bg-orange-50'
                : 'text-gray-400 hover:text-green-600 hover:bg-green-50'
            } ${toggleActiveMutation.isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
            title={event.isActive ? 'השבת אירוע' : 'הפעל אירוע'}
          >
            <Power className="h-4 w-4" />
          </button>

          <button
            onClick={() => handleDeleteEvent(event)}
            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
            title="מחק אירוע"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="md:flex md:items-center md:justify-between">
          <div className="min-w-0 flex-1">
            <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:truncate sm:text-3xl">
              אירועים
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              נהל את כל האירועים שלך במקום אחד
            </p>
          </div>
          <div className="mt-4 flex md:ml-4 md:mt-0">
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500"
            >
              <Plus className="h-4 w-4 ml-2" />
              אירוע חדש
            </button>
          </div>
        </div>

        {/* Events Grid */}
        {isLoading ? (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 animate-pulse">
                <div className="space-y-3">
                  <div className="h-6 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                  <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                </div>
              </div>
            ))}
          </div>
        ) : events && events.length > 0 ? (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {events.map((event) => (
              <EventCard key={event._id} event={event} />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <Calendar className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">אין אירועים</h3>
            <p className="mt-1 text-sm text-gray-500">התחל על ידי יצירת האירוע הראשון שלך</p>
            <div className="mt-6">
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500"
              >
                <Plus className="h-4 w-4 ml-2" />
                צור אירוע חדש
              </button>
            </div>
          </div>
        )}

        {/* QR Code Modal */}
        {selectedEvent && (
          <QRCodeModal 
            event={selectedEvent} 
            onClose={() => setSelectedEvent(null)} 
          />
        )}

        {/* Create Event Modal */}
        {showCreateModal && (
          <CreateEventModal onClose={() => setShowCreateModal(false)} />
        )}
      </div>
    </Layout>
  );
};

// QR Code Modal Component
const QRCodeModal = ({ event, onClose }: { event: Event; onClose: () => void }) => {
  const { data: qrData, isLoading } = useQuery(
    ['qr', event._id],
    () => axios.get(`/api/qr/event/${event._id}`).then(res => res.data.data)
  );

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-25" onClick={onClose} />
        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
          <div className="text-center">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              QR Code - {event.name}
            </h3>
            
            {isLoading ? (
              <div className="flex justify-center py-8">
                <div className="loading-spinner"></div>
              </div>
            ) : qrData ? (
              <div className="space-y-4">
                <img 
                  src={qrData.qrCode} 
                  alt="QR Code" 
                  className="mx-auto border rounded-lg"
                />
                <div className="text-sm text-gray-600">
                  <p className="font-medium">קישור לאתר:</p>
                  <p className="break-all">{qrData.url}</p>
                </div>
                <button
                  onClick={() => {
                    const link = document.createElement('a');
                    link.download = `qr-${event.name}.png`;
                    link.href = qrData.qrCode;
                    link.click();
                  }}
                  className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
                >
                  הורד QR Code
                </button>
              </div>
            ) : (
              <p className="text-gray-500">שגיאה בטעינת QR Code</p>
            )}
            
            <button
              onClick={onClose}
              className="mt-4 w-full bg-gray-200 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-300 transition-colors"
            >
              סגור
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Create Event Modal Component
const CreateEventModal = ({ onClose }: { onClose: () => void }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    bride: '',
    groom: '',
    eventDate: '',
    venueName: '',
    venueAddress: '',
    venueCity: ''
  });

  const queryClient = useQueryClient();

  const createEventMutation = useMutation(
    (data: any) => axios.post('/api/events', data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('events');
        toast.success('אירוע נוצר בהצלחה');
        onClose();
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'שגיאה ביצירת האירוע');
      },
    }
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const eventData = {
      name: formData.name,
      description: formData.description,
      coupleNames: {
        bride: formData.bride,
        groom: formData.groom
      },
      eventDate: formData.eventDate,
      venue: {
        name: formData.venueName,
        address: formData.venueAddress,
        city: formData.venueCity
      }
    };

    createEventMutation.mutate(eventData);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-25" onClick={onClose} />
        <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">צור אירוע חדש</h3>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                שם האירוע *
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  שם הכלה *
                </label>
                <input
                  type="text"
                  required
                  value={formData.bride}
                  onChange={(e) => setFormData(prev => ({ ...prev, bride: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  שם החתן *
                </label>
                <input
                  type="text"
                  required
                  value={formData.groom}
                  onChange={(e) => setFormData(prev => ({ ...prev, groom: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                תאריך האירוע *
              </label>
              <input
                type="date"
                required
                value={formData.eventDate}
                onChange={(e) => setFormData(prev => ({ ...prev, eventDate: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                תיאור
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                שם האולם
              </label>
              <input
                type="text"
                value={formData.venueName}
                onChange={(e) => setFormData(prev => ({ ...prev, venueName: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="flex gap-4 pt-4">
              <button
                type="submit"
                disabled={createEventMutation.isLoading}
                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {createEventMutation.isLoading ? 'יוצר...' : 'צור אירוע'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="flex-1 bg-gray-200 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-300 transition-colors"
              >
                ביטול
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Events;
